import { useState } from 'react';
import { FileText, Send, Loader2, Trash2 } from 'lucide-react';
import { api, fmtDate } from './api';
import { useDialog } from '../../../hooks/useDialog';
import { useLanguage } from '../../../context/LanguageContext';

export default function NotesTab({ leadId, notes = [], onChanged, currentUser }) {
  const { t } = useLanguage();
  const dialog = useDialog();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api(`/api/leads/${leadId}/aktivitaet`, { method: 'POST', body: { typ: 'notiz', inhalt: text.trim() } });
      setText('');
      onChanged?.();
    } catch (e) {
      alert('Notiz konnte nicht gespeichert werden: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const canDelete = (note) => {
    if (!currentUser) return false;
    const role = currentUser.rolle;
    if (['administrator', 'ops_manager', 'cs_teamlead'].includes(role)) return true;
    return note.erstellt_von && note.erstellt_von === currentUser.mitarbeiter_id;
  };

  const remove = async (note) => {
    const ok = await dialog.confirm(
      t('salesNotes.deleteTitle') || 'Notiz löschen?',
      t('salesNotes.deleteConfirm') || 'Diese Notiz wirklich löschen? Diese Aktion ist nicht umkehrbar.',
      { confirmLabel: t('common.delete') || 'Löschen', cancelLabel: t('common.cancel') || 'Abbrechen', variant: 'danger' }
    );
    if (!ok) return;
    setDeletingId(note.id);
    try {
      await api(`/api/sales/aktivitaeten/${note.id}`, { method: 'DELETE' });
      onChanged?.();
    } catch (e) {
      await dialog.alert(t('common.error') || 'Fehler', (t('salesNotes.deleteFailed') || 'Notiz konnte nicht gelöscht werden') + ': ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-3">
      {dialog.element}
      <div className="border border-[var(--border-card)] rounded-lg bg-[var(--bg-card)] p-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Neue Notiz hinzufügen…"
          rows={3}
          className="w-full text-[13px] bg-transparent resize-none focus:outline-none text-[var(--text-primary)]"
        />
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[var(--border-card)]/50">
          <span className="text-[11px] text-[var(--text-tertiary)]">Markdown unterstützt</span>
          <button
            onClick={submit}
            disabled={saving || !text.trim()}
            className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded bg-[#338edc] text-white text-[12px] hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Notiz speichern
          </button>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--text-tertiary)]">
          <FileText className="w-8 h-8 mb-2 opacity-50" />
          <span className="text-[13px]">Noch keine Notizen</span>
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const showDelete = canDelete(n);
            const isDeleting = deletingId === n.id;
            return (
              <div key={n.id} className="group border border-[var(--border-card)] rounded-lg bg-[var(--bg-card)] p-3 relative">
                <div className="flex items-center gap-2 text-[11px] text-[var(--text-tertiary)] mb-1">
                  <span className="font-medium text-[var(--text-secondary)]">{n.erstellt_von_name || 'System'}</span>
                  <span className="ml-auto">{fmtDate(n.erstellt_am)}</span>
                  {showDelete && (
                    <button
                      onClick={() => remove(n)}
                      disabled={isDeleting}
                      title={t('salesNotes.delete') || 'Notiz löschen'}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-600 disabled:opacity-50"
                    >
                      {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </button>
                  )}
                </div>
                <div className="text-[13px] text-[var(--text-primary)] whitespace-pre-wrap">{n.inhalt}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
